import React, { useState } from 'react';
import { AppConfig, getConfig, KeyboardDifficulty } from '../config';
import { RealVoiceService } from '../services/VoiceService';
import { MESSAGES } from '../config/messages';

interface StartScreenProps {
    onStart: (config: Partial<AppConfig>) => void;
    addDebugLog: (message: string) => void;
}

export const StartScreen: React.FC<StartScreenProps> = ({ 
    onStart,
    addDebugLog
}) => {
    const config = getConfig();
    const [fullConversationMode, setFullConversationMode] = useState(config.game.fullConversationMode);
    const [showKeyboardHints, setShowKeyboardHints] = useState(config.ui.showKeyboardHints);
    const [showFullWordDisplay, setShowFullWordDisplay] = useState(config.ui.showFullWordDisplay);
    const [showVirtualKeyboard, setShowVirtualKeyboard] = useState(config.ui.showVirtualKeyboard);
    const [keyboardDifficulty, setKeyboardDifficulty] = useState<KeyboardDifficulty>(config.ui.keyboardDifficulty);
    const [showSettings, setShowSettings] = useState(false);

    const handleStart = async () => {
        // First, perform the voice test with the new sentence
        try {
            addDebugLog('🧪 Starting voice test on Start Learning...');
            
            const voiceService = new RealVoiceService();
            await voiceService.initialize();
            addDebugLog('✅ VoiceService initialized for voice test');
            
            // Test TTS with the new sentence
            const testText = MESSAGES.LETS_START;
            addDebugLog('🗣️ Testing TTS with text: ' + testText);
            
            const startTime = Date.now();
            await voiceService.testTTSOnly(testText);
            const endTime = Date.now();
            
            const duration = endTime - startTime;
            addDebugLog(`✅ Voice test completed in ${duration}ms`);
            console.log('✅ Voice test completed in', duration, 'ms');
            
        } catch (error) {
            const errorMsg = error instanceof Error ? error.message : 'שגיאה לא ידועה';
            addDebugLog('❌ Voice test failed: ' + errorMsg);
            console.warn('Voice test failed, continuing anyway:', error);
        }

        // Then proceed with starting the game
        const config: Partial<AppConfig> = {
            game: { fullConversationMode },
            ui: { showKeyboardHints, showFullWordDisplay, showVirtualKeyboard, keyboardDifficulty }
        };
        onStart(config);
    };

    const ToggleSwitch: React.FC<{ 
        value: boolean; 
        onChange: (value: boolean) => void; 
        label: string;
        description: string;
    }> = ({ value, onChange, label, description }) => (
        <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px',
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            marginBottom: '12px'
        }}>
            <div style={{ direction: 'rtl', flex: 1 }}>
                <div style={{ fontWeight: 'bold', fontSize: '16px', color: '#374151' }}>
                    {label}
                </div>
                <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
                    {description}
                </div>
            </div>
            <button
                onClick={() => onChange(!value)}
                style={{
                    width: '60px',
                    height: '32px',
                    borderRadius: '16px',
                    border: 'none',
                    backgroundColor: value ? '#10b981' : '#d1d5db',
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    marginLeft: '16px'
                }}
            >
                <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    backgroundColor: 'white',
                    position: 'absolute',
                    top: '4px',
                    left: value ? '32px' : '4px',
                    transition: 'all 0.3s',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }} />
            </button>
        </div>
    );

    return (
    <div style={{
        backgroundColor: 'white',
        padding: '48px',
        borderRadius: '24px',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        textAlign: 'center',
        maxWidth: '600px',
        margin: '0 auto'
    }}>
        <div style={{ fontSize: '96px', marginBottom: '24px' }}>🎯</div>
        
        <h1 style={{
            fontSize: '36px',
            fontWeight: 'bold',
            color: '#1f2937',
            marginBottom: '16px',
            direction: 'rtl'
        }}>
            בוא נתרגל הקלדה!
        </h1>
        
        <p style={{
            fontSize: '20px',
            color: '#6b7280',
            marginBottom: '32px',
            direction: 'rtl'
        }}>
            המורה הוירטואלי יעזור לך ללמוד להקליד מילים בעברית
        </p>

        <button
            onClick={handleStart}
            style={{
                padding: '20px 48px',
                fontSize: '24px',
                fontWeight: 'bold',
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '16px',
                cursor: 'pointer',
                boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
                transition: 'all 0.3s',
                marginBottom: '24px'
            }}
        >
            🚀 התחל ללמוד!
        </button>
        
        <p style={{
            fontSize: '14px',
            color: '#9ca3af',
            marginBottom: '32px',
            direction: 'rtl'
        }}>
            לחץ כדי להפעיל את השירותים הקוליים ולהתחיל
        </p>

        {/* Collapsible Settings Panel */}
        <div style={{
            backgroundColor: '#f3f4f6',
            borderRadius: '12px',
            marginBottom: '24px'
        }}>
            <button
                onClick={() => setShowSettings(!showSettings)}
                style={{
                    width: '100%',
                    padding: '16px 24px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: '#374151',
                    transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#e5e7eb';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                }}
            >
                <span style={{ direction: 'rtl' }}>⚙️ הגדרות למידה</span>
                <span style={{ transform: showSettings ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }}>
                    ▼
                </span>
            </button>
            
            {showSettings && (
                <div style={{
                    padding: '0 24px 24px 24px',
                    animation: 'slideDown 0.3s ease-out'
                }}>
                    <style>
                        {`
                            @keyframes slideDown {
                                from {
                                    opacity: 0;
                                    transform: translateY(-10px);
                                }
                                to {
                                    opacity: 1;
                                    transform: translateY(0);
                                }
                            }
                        `}
                    </style>
                    
                    <ToggleSwitch
                        value={fullConversationMode}
                        onChange={setFullConversationMode}
                        label="שיחה מלאה עם המורה"
                        description="כולל בקשת שם ושיחה לפני התחלת הקלדה"
                    />
                    
                    <ToggleSwitch
                        value={showKeyboardHints}
                        onChange={setShowKeyboardHints}
                        label="רמזים על המקלדת"
                        description="הצגת הדרכה איפה למצוא כל אות"
                    />
                    
                    <ToggleSwitch
                        value={showFullWordDisplay}
                        onChange={setShowFullWordDisplay}
                        label="הצגת המילה המלאה"
                        description="הראה את כל המילה או רק אותיות שהוקלדו"
                    />
                    
                    <ToggleSwitch
                        value={showVirtualKeyboard}
                        onChange={setShowVirtualKeyboard}
                        label="שימוש במקלדת וירטואלית"
                        description="הצגת מקלדת על המסך או שימוש במקלדת הפיזית בלבד"
                    />
                    
                    {showVirtualKeyboard && (
                        <div style={{
                            padding: '16px',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            marginBottom: '12px',
                            backgroundColor: '#f8fafc'
                        }}>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                gap: '8px',
                                direction: 'rtl'
                            }}>
                                <button
                                    onClick={() => setKeyboardDifficulty('full')}
                                    style={{
                                        padding: '8px 16px',
                                        borderRadius: '6px',
                                        border: keyboardDifficulty === 'full' ? '2px solid #0ea5e9' : '1px solid #d1d5db',
                                        backgroundColor: keyboardDifficulty === 'full' ? '#0ea5e9' : 'white',
                                        color: keyboardDifficulty === 'full' ? 'white' : '#374151',
                                        cursor: 'pointer',
                                        fontWeight: 'bold',
                                        fontSize: '14px',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    מלאה
                                </button>
                                
                                <button
                                    onClick={() => setKeyboardDifficulty('challenging')}
                                    style={{
                                        padding: '8px 16px',
                                        borderRadius: '6px',
                                        border: keyboardDifficulty === 'challenging' ? '2px solid #0ea5e9' : '1px solid #d1d5db',
                                        backgroundColor: keyboardDifficulty === 'challenging' ? '#0ea5e9' : 'white',
                                        color: keyboardDifficulty === 'challenging' ? 'white' : '#374151',
                                        cursor: 'pointer',
                                        fontWeight: 'bold',
                                        fontSize: '14px',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    מאתגרת
                                </button>
                                
                                <button
                                    onClick={() => setKeyboardDifficulty('adaptive')}
                                    style={{
                                        padding: '8px 16px',
                                        borderRadius: '6px',
                                        border: keyboardDifficulty === 'adaptive' ? '2px solid #0ea5e9' : '1px solid #d1d5db',
                                        backgroundColor: keyboardDifficulty === 'adaptive' ? '#0ea5e9' : 'white',
                                        color: keyboardDifficulty === 'adaptive' ? 'white' : '#374151',
                                        cursor: 'pointer',
                                        fontWeight: 'bold',
                                        fontSize: '14px',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    מותאמת
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
        
    </div>
    );
};